<template>
    <div class="participant-metrics-chart-time-series">
        <highcharts :options="chartOptions"></highcharts>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import moment from 'moment'
import Highcharts from 'highcharts'
import MetricUtils from '@/providers/Metrics'
import GaugeColors from '@/providers/GaugeColors'

/**
 * Component to display activity metrics, for a time series using the format:
 * ['dateTime' => '2020-09-21', 'value' => '10530']
 * for each data point
 */
export default Vue.extend({
    name: 'ParticipantMetricsChartTimeSeries',
    props: {
        metrics: Array,
        metric: String,
        service: String,
        pointValueMultiplier: Number, // to convert units - e.g. for kg to lb, 2.2
        highlightStart: String, //Date string to start highlight of unchecked dates
        dateRange: Object
    },
    data() {
        return {
            //The order here corresponds to the 'series' array below to get correct color
            services: ['MANUAL', 'FITBIT', 'GOOGLE_FIT', 'APPLE', 'GOAL'],
            series: [
                {
                    name: 'Manual Entry',
                    data: [],
                    color: GaugeColors.METRIC_SERIES_COLORS[0]
                },
                {
                    name: 'Fitbit',
                    data: [],
                    color: GaugeColors.METRIC_SERIES_COLORS[1]
                },
                {
                    name: 'Google',
                    data: [],
                    color: GaugeColors.METRIC_SERIES_COLORS[2]
                },
                {
                    name: 'Apple',
                    data: [],
                    color: GaugeColors.METRIC_SERIES_COLORS[3]
                },
                {
                    name: 'Goal',
                    data: [],
                    color: GaugeColors.METRIC_GOAL_LINE_COLOR
                },
            ],
            decimalPlaces: 1,
        }
    },
    computed: {
        /**
         * Returns an array of metric object containing readings for each service.
         * If a service listed in this.services is not connected, that index will be undefined
         */
        selectedMetric() {
            const metrics = []
            this.services.forEach((service, index) => {
                metrics[index] = _.find(this.metrics, (metric) => {
                    return (metric.metric === this.metric) && (metric.service === service)
                })
            })
            return metrics
        },
        chartOptions() {
            const options = this.getBaseOptions()
            this.addMetricOptions(options)
            return options
        },
    },
    methods: {
        getBaseOptions() {
            const PROGRESS = this.$i18n.gt('LABEL_MY_HABITS_PROGRESS')
            Highcharts.setOptions({lang: {thousandsSep: ','}})
            let tooltipOptions = { enabled: true }
            let yaxisLabels = { align: 'left', x: -30 }
            if (this.metric === 'SLEEP') {
                tooltipOptions['formatter'] = function () {
                    let date = new Date(this.x)
                    console.log('ParticipantMetricsChartTimeSeries  this=', this, '  date=', date);
                    let options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'}
                    let hours = Math.floor(this.y / 60);
                    let minutes = this.y % 60;
                    let value = (hours ? hours.toString() : '') + ':' + ((minutes < 10) ? '0' : '') + minutes.toString();
                    return date.toLocaleDateString('en-US', options) + '<br/><strong>' + value + ' hours</strong>';
                }
                yaxisLabels['formatter'] = function (context) {
                    if (context.value) {
                        let hours = Math.floor(context.value / 60);
                        let minutes = context.value % 60;
                        return (hours ? hours.toString() : '') + ':' + ((minutes < 10) ? '0' : '') + minutes.toString();
                    } else {
                        return '';
                    }
                }
            } else {
                tooltipOptions['pointFormat'] = `<strong>{point.y:,.${this.decimalPlaces}f}</strong> ${MetricUtils.unitLabels()[this.metric]}`,
                yaxisLabels['format'] = `{value:,.0f}` ;
            }
            return {
                chart: {
                    borderColor: '#fff',
                    borderWidth: 4,
                    type: 'line',
                    height: (this.$mq === 'sm' ? '80%' : '50%'),
                    showAxes: true,
                    backgroundColor: '#eee',
                    plotBackgroundColor: '#eee',
                    margin: 40,
                    marginBottom: 50
                },
                title: null,
                credits: {enabled: false},
                tooltip: tooltipOptions,
                responsive: {
                    rules: [{
                        chartOptions: {
                            legend: {
                                enabled: false
                            }
                        },
                        condition: {
                            maxWidth: 300,
                            minWidth: 100
                        }
                    }]
                },

                plotOptions: {
                    series: {
                        color: `rgb(128, 128, 128, 1)`,
                        marker: {
                            symbol: 'circle',
                            radius: 3,
                        },
                    }
                },

                xAxis: {
                    visible: false,
                    type: 'datetime',
                    labels: {
                        format: "{value:%Y-%m-%d}"
                    },
                },

                yAxis: {
                    visible: true,
                    gridLineWidth: 0,
                    labels: yaxisLabels,
                    title: null,
                },

                series: []
            }
        },
        addMetricOptions(options) {
            const multiplier = this.pointValueMultiplier || 1

            if ((this.metric === 'ACTIVITY') || (this.metric === 'HEARTRATE') || (this.metric === 'HRV')) {
                this.decimalPlaces = 0
            }

            let seriesIdx = 0//Keeps track # series used, set this back to 1 if using a dummy series

            this.selectedMetric.forEach((metric, index) => {
                if (metric) {
                    //Clone array since otherwise the goal value is modified in the source array
                    let readings = _.cloneDeep(metric.readings)
                    if (metric.service === "GOAL") {
                        if (readings[0]) {
                            let goal = readings[0].value
                            options.yAxis.plotLines = [{
                                color: GaugeColors.METRIC_GOAL_LINE_COLOR,
                                width: 1.5,
                                dashStyle: 'longdashdot',
                                value: goal.value,
                                label: {text: goal.dateTime}
                            }]
                            // modify goal data point to left of date range
                            if (this.dateRange && this.dateRange.from)
                                readings[0].value.dateTime = this.dateRange.from
                        }
                    } else if (this.dateRange && this.dateRange.from && this.dateRange.to) {
                        const dateFrom = this.dateRange.from
                        const dateTo = this.dateRange.to
                        readings = _.filter(readings, reading => {
                            return (reading.value.dateTime >= dateFrom) && (reading.value.dateTime <= dateTo)
                        })
                    }
                    //Sort readings by date for clean line plots
                    readings = readings.sort((a, b) => {
                        return new Date(a.value.dateTime) - new Date(b.value.dateTime)
                    })
                    //When metric isn't null, add the series to the chart
                    options.series.push(this.series[index])
                    //Use seriesIdx instead of index since chart may not have all series
                    options.series[seriesIdx].data = _.map(readings, (reading) => {
                        let value = parseFloat(reading.value.value) * multiplier
                        let date = Date.parse(reading.value.dateTime)

                        let marker = {
                            enabled: false,
                        }
                        //Highlights all when no start date is present (i.e. no RPM Checks yet)
                        if (!this.highlightStart || date > Date.parse(this.highlightStart)) {
                            marker = {
                                enabled: true,
                                // fillColor: GaugeColors.METRIC_UNBILLED_COLOR,
                            }
                        }

                        return {
                            x: Date.parse(reading.value.dateTime),
                            y: value,
                            marker: marker
                        }
                    })
                    seriesIdx++
                }
            })

        }
    }
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.participant-metrics-time-series {
}
</style>
